import type { ColumnKind } from '../lib/approval-formula';
import { apiClient } from './client';

/**
 * 문서 종류 레지스트리 API — backend `modules/approval/doc-type.routes.ts` (결재 A1, 2026-08-24).
 *
 * 설계: *"근태 다섯이 첫 입주자일 뿐입니다. 새 종류는 `approval_doc_types` 에 **행 하나로**
 * 열립니다 — 결재 로직은 하나를 같이 씁니다."*
 *
 * ★ 옛 서버에는 이 라우트가 없다(404). 부르는 쪽이 실패를 삼키고 종전 목록으로 되돌아간다.
 */

export type DocTypeCategory = 'HR' | 'GENERAL';
/** 전결을 **무엇으로 재나** (결재 A3, 2026-08-24). */
export type FinalizeKind = 'NONE' | 'DAYS' | 'AMOUNT';

/**
 * 서식 — 이 종류의 칸 정의 (2026-08-28 · 자리 재정의 판 9).
 *
 * ★★ **칸 종류는 회사가 못 만든다** — 아홉이 전부이고 서버 `form-schema.ts` 가 SSOT 다.
 * ★★ **「고르기」의 목록은 여기 안 담는다** — 주인(근태 기준 설정 · 조직도)을 가리키기만 한다.
 */
export const FIELD_KINDS = [
  'text', 'textarea', 'number', 'date', 'period', 'select', 'user', 'table',
  // ★ 시각·계산식 (2026-09-01) — **끝에만** 는다. 저장된 서식이 가리키는 값이라 순서를 못 바꾼다.
  'time', 'formula',
] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export type FieldSource =
  | { kind: 'inline'; options: string[] }
  | { kind: 'company_code'; setKey: string }
  | { kind: 'org'; level: 'division' | 'team' };

export interface FormField {
  key: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  hint?: string;
  source?: FieldSource;
  columns?: string[];
  /**
   * `table` 전용 — **열마다의 종류** (판 13). `columns` 와 같은 길이.
   * ★ `columns` 를 객체로 바꾸지 않고 나란히 둔다 — 바꾸면 옛 화면이 표 머리를 그리다 죽는다.
   */
  columnKinds?: ColumnKind[];
  /**
   * `table` 전용 — **`select` 열의 목록 주인** (2026-09-04 · 예산 판 B0).
   * `columns` 와 같은 길이이고, 고르기가 아닌 열은 `null`.
   */
  columnSources?: (FieldSource | null)[];
  /** `formula` 전용 — 식. 값은 **서버가 다시 센다**. */
  expr?: string;
  /** `formula` 전용 — 보이는 꼴. */
  format?: 'number' | 'money' | 'hours';
  /** `formula` 전용 — 소수 자릿수. */
  decimals?: number;
  /** 전결 기준으로 쓸 칸 — 종류마다 **하나만**, 숫자·계산식 칸에만. */
  measure?: boolean;
}

export interface FormSchemaDto {
  fields: FormField[];
}

export interface DocTypeDto {
  id: string;
  /** `approval_documents.type` 에 그대로 들어가는 코드. **만든 뒤에는 못 바꾼다.** */
  code: string;
  name: string;
  /** 기본 결재 단계 수. 조직 사다리가 더 길면 사다리를 따른다. */
  defaultSteps: number;
  /** 기안자가 결재선을 고칠 수 있는가. 끄면 **서버가 조직에서 다시 뽑는다**. */
  allowDrafterEdit: boolean;
  category: string;
  isActive: boolean;
  sortOrder: number;
  /**
   * 서식 (2026-08-28 · 판 9) — `null`/`undefined` 면 **제목·본문만 받는 종전 모습**이다.
   * 옛 서버는 이 칸을 안 준다.
   */
  formSchema?: FormSchemaDto | null;
  /**
   * 이 서식은 **회사가 못 고친다** (2026-09-01 · 사용자 지시).
   *
   * ★ 근태가 이 칸들을 **재료로 읽는다** — 「기간」이 시작·끝 두 날짜여야 휴가 기록으로
   *   옮길 수 있다. 어떤 회사가 그것을 날짜 하나로 바꿔 두어, 결재가 끝나도 **연차가 1분도
   *   안 깎이는** 상태였다.
   * ★ 옛 서버는 이 칸을 안 준다 — `undefined` 면 종전처럼 열려 있다(서버가 따로 막는다).
   */
  schemaLocked?: boolean;
  /**
   * 전결 — 기준 **이하**이면 윗단계를 자동으로 뺀다 (결재 A3).
   * *"반차 하나에 대표까지 갈 필요가 없다."* 옛 서버는 안 준다.
   */
  finalizeKind?: FinalizeKind;
  finalizeThreshold?: number | null;
  /** 전결일 때 남길 단계 수. */
  finalizeSteps?: number;
  /**
   * 공통 양식인가, 이 회사가 만든 것인가 (2026-09-01 · 판 13 ⑤).
   * ★ **파생이다** — 표에 칸이 없다. 그 코드가 공통 카탈로그에 있으면 `COMMON`.
   * ★ 옛 서버는 안 준다 → 화면이 종전대로 한 묶음으로 그린다.
   */
  origin?: 'COMMON' | 'COMPANY';
  /** 공통인데 이 회사가 손댔나 — 그때만 「되돌리기」가 뜻이 있다. */
  overridden?: boolean;
  /**
   * ── 회사 관리자가 정하는 셋 (2026-09-07 · 시안 A1) ─────────────────────────
   *
   * ★★★ `boxCode` 가 「결재양식」 팝업의 **왼쪽 묶음**이자 전사결재함이다 —
   *    기안자가 고르는 칸이 아니다. `null` 이면 「묶음 없음」으로 모인다.
   * ★ `retentionYears` 가 `null` 이면 기본 **5년**.
   * ★ `defaultTitle` 이 `null` 이면 **양식 이름**을 제목에 미리 채운다.
   * ★ 옛 서버는 이 셋을 안 준다 → `undefined`.
   */
  boxCode?: string | null;
  retentionYears?: number | null;
  defaultTitle?: string | null;
}

export interface DocTypeInput {
  /**
   * ★★★ **안 주면 서버가 짓는다** (2026-09-01 · 판 13 ④) — `TIMESOFT-0001`.
   *    이 값은 만든 뒤 못 바꾸므로, 손으로 짓게 하지 않는다. 다른 시스템과 맞춰야 할 때만 준다.
   * ★ 옛 서버는 필수라 안 주면 400 이다 — 그때는 화면이 「코드 직접 정하기」로 받는다.
   */
  code?: string;
  name: string;
  defaultSteps?: number;
  allowDrafterEdit?: boolean;
  category?: DocTypeCategory;
  sortOrder?: number;
  finalizeKind?: FinalizeKind;
  finalizeThreshold?: number | null;
  finalizeSteps?: number;
  /**
   * 서식 (2026-08-28 · 판 9) — `null` 을 보내면 «서식 없음»으로 되돌린다.
   * ★ **있을 때만 싣는다** — 옛 서버는 이 칸을 모르고 `additionalProperties:false` 라 400 이다.
   */
  formSchema?: FormSchemaDto | null;
  /** 회사 관리자가 정하는 셋 (2026-09-07). `null` 을 보내면 «안 정함»으로 되돌린다. */
  boxCode?: string | null;
  retentionYears?: number | null;
  defaultTitle?: string | null;
}

/** `all=true` 는 관리자만 뜻이 있다 — 꺼 둔 종류까지 본다(기안 화면은 켜진 것만). */
export async function listDocTypes(all = false): Promise<DocTypeDto[]> {
  const { data } = await apiClient.get<{ items: DocTypeDto[] }>('/approval/doc-types', {
    params: all ? { all: '1' } : undefined,
  });
  return data.items;
}

export async function createDocType(input: DocTypeInput): Promise<DocTypeDto> {
  const { data } = await apiClient.post<{ docType: DocTypeDto }>('/approval/doc-types', input);
  return data.docType;
}

/**
 * 기본으로 되돌리기 — **공통 양식에 얹은 행을 지운다** (판 13 ⑤).
 *
 * ★ 바닥(카탈로그)은 건드린 적이 없으므로 「원래 값이 뭐였더라」를 기억할 필요가 없다.
 * ★ 회사가 만든 양식에는 못 쓴다(서버가 400 `NOT_COMMON`) — 안 쓰려면 끈다.
 */
export async function resetDocType(id: string): Promise<{ reset: boolean }> {
  const { data } = await apiClient.post<{ reset: boolean }>(`/approval/doc-types/${id}/reset`);
  return data;
}

/** 코드는 보내지 않는다 — 서버가 400 `CODE_IMMUTABLE` 로 막는다. */
export async function updateDocType(
  id: string,
  patch: Partial<Omit<DocTypeInput, 'code'>> & { isActive?: boolean },
): Promise<DocTypeDto> {
  const { data } = await apiClient.patch<{ docType: DocTypeDto }>(`/approval/doc-types/${id}`, patch);
  return data.docType;
}
